import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useExternalLink } from './ExternalLinkWarning';
import './SearchBar.css';
import { getAllSearchEntries } from '../lib/searchRegistry';

const SearchBar = () => {
  const { t, isMarathi } = useLanguage();
  const { showWarning } = useExternalLink();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthesisRef = useRef(null);

  // Static page index for client-side search (add/update as pages evolve)
  const PAGES = useRef([
    {
      id: 'home',
      url: '/',
      title: { marathi: 'मुख्यपृष्ठ', english: 'Home' },
      description: { marathi: 'मुख्य पृष्ठ', english: 'Main landing page' },
      keywords: ['home', 'मुख्य']
    },
    { id: 'overview', url: '/overview', title: { marathi: 'आमच्याविषयी', english: 'About Us' }, description: { marathi: 'मंडळाची माहिती', english: 'Information about Maharashtra Labour Welfare Board' }, keywords: ['about', 'overview', 'आढावा'] },
    { id: 'administrative-structure', url: '/administrative-structure', title: { marathi: 'प्रशासकिय रचना', english: 'Administrative Structure' }, description: { marathi: 'रचना व माहिती', english: 'Structure and information' }, keywords: [] },
    { id: 'board-project', url: '/board-project', title: { marathi: 'मंडळ प्रकल्प', english: 'Board Project' }, description: { marathi: 'मंडळाचे प्रकल्प', english: 'Board projects' }, keywords: ['project', 'प्रकल्प'] },
    { id: 'annual-report', url: '/annual-report', title: { marathi: 'वार्षिक गोषवारा', english: 'Annual Report' }, description: { marathi: 'वार्षिक अहवाल', english: 'Yearly report' }, keywords: [] },
    { id: 'circulars', url: '/circulars', title: { marathi: 'कर्मचाऱ्यांसाठी परिपत्रके', english: 'Circulars for Employees' }, description: { marathi: 'परिपत्रके', english: 'Circulars' }, keywords: [] },
    { id: 'workers-welfare', url: '/workers-welfare', title: { marathi: 'कामगार कल्याण निधी', english: "Labours' Welfare Fund" }, description: { marathi: 'अधिनियम आणि माहिती', english: 'Act and Information' }, keywords: [] },
    { id: 'schemes', url: '/schemes', title: { marathi: 'योजना', english: 'Schemes' }, description: { marathi: 'योजना', english: 'Schemes' }, keywords: [] },
    { id: 'activities', url: '/activities', title: { marathi: 'उपक्रम', english: 'Activities' }, description: { marathi: 'उपक्रम', english: 'Activities' }, keywords: [] },
    { id: 'programs', url: '/programs', title: { marathi: 'कार्यक्रम', english: 'Programs' }, description: { marathi: 'कार्यक्रम', english: 'Programs' }, keywords: [] },
    { id: 'awards', url: '/awards', title: { marathi: 'पुरस्कार', english: 'Awards' }, description: { marathi: 'पुरस्कार', english: 'Awards' }, keywords: [] },
    { id: 'right-to-info', url: '/right-to-info', title: { marathi: 'माहितीचा अधिकार', english: 'Right to Information' }, description: { marathi: 'RTI', english: 'RTI' }, keywords: ['RTI'] },
    { id: 'tenders-notices', url: '/tenders-notices', title: { marathi: 'विभागीय निविदा आणि सूचना', english: 'Departmental Tenders & Notices' }, description: { marathi: 'निविदा', english: 'Tenders' }, keywords: [] },
    { id: 'photo-gallery', url: '/photo-gallery', title: { marathi: 'फोटो गॅलरी', english: 'Photo Gallery' }, description: { marathi: 'छायाचित्रे', english: 'Photos' }, keywords: ['gallery', 'photos', 'photo'] },
    { id: 'video-gallery', url: '/video-gallery', title: { marathi: 'व्हिडिओ गॅलरी', english: 'Video Gallery' }, description: { marathi: 'व्हिडिओ', english: 'Videos' }, keywords: ['gallery', 'videos', 'video'] },
    { id: 'faq', url: '/faq', title: { marathi: 'वारंवार विचारले जाणारे प्रश्न', english: 'FAQ' }, description: { marathi: 'प्रश्नोत्तरे', english: 'Frequently asked questions' }, keywords: ['help'] },
    { id: 'site-map', url: '/site-map', title: { marathi: 'साईट नकाशा', english: 'Site Map' }, description: { marathi: 'साईट नकाशा', english: 'Site map' }, keywords: ['sitemap'] },
    { id: 'feedback', url: '/feedback', title: { marathi: 'अभिप्राय', english: 'Feedback' }, description: { marathi: 'अभिप्राय फॉर्म', english: 'Feedback form' }, keywords: [] },
    { id: 'contact', url: '/contact', title: { marathi: 'संपर्क', english: 'Contact Us' }, description: { marathi: 'संपर्क साधा', english: 'Get in touch' }, keywords: ['phone', 'email'] },
    { id: 'list-of-services', url: '/list-of-services', title: { marathi: 'सेवांची यादी', english: 'List of Services' }, description: { marathi: 'सेवांची यादी', english: 'List of Services' }, keywords: ['services', 'list'] },
    { id: 'dashboard', url: '/dashboard', title: { marathi: 'डॅशबोर्ड', english: 'Dashboard' }, description: { marathi: 'डॅशबोर्ड', english: 'Dashboard' }, keywords: ['dashboard'] },
    { id: 'privacy', url: '/privacy', title: { marathi: 'गोपनीयता धोरण आणि अस्वीकरण', english: 'Privacy Policy and Disclaimer' }, description: { marathi: 'गोपनीयता धोरण', english: 'Privacy Policy' }, keywords: ['privacy', 'policy', 'disclaimer', 'privacy policy'] },
  ]).current;

  // Helpers for search and highlighting
  const normalize = useCallback((s = '') => {
    const str = (s || '').toString();
    try {
      return isMarathi() ? str.toLocaleLowerCase('mr-IN') : str.toLocaleLowerCase();
    } catch {
      return str.toLowerCase();
    }
  }, [isMarathi]);
  
  const tokenize = useCallback((s = '') => normalize(s).split(/\s+/).filter(Boolean), [normalize]);
  
  const renderHighlighted = (text, query) => {
    const tokens = tokenize(query);
    if (!tokens.length || !text) return text;
    const escaped = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    // Unicode-aware, case-insensitive
    const pattern = new RegExp(`(${escaped})`, 'iu');
    const parts = String(text).split(pattern);
    return parts.map((part, idx) => (
      idx % 2 === 1 ? <mark key={idx}>{part}</mark> : <React.Fragment key={idx}>{part}</React.Fragment>
    ));
  };

  // Text-to-Speech function
  const speakText = useCallback((text, lang = null) => {
    // Stop any ongoing speech
    if (synthesisRef.current) {
      window.speechSynthesis.cancel();
    }

    if (!('speechSynthesis' in window)) {
      console.warn('Text-to-speech not supported in this browser');
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    const language = lang || (isMarathi() ? 'mr-IN' : 'en-US');
    
    utterance.lang = language;
    utterance.rate = 0.9; // Slightly slower for clarity
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      synthesisRef.current = null;
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setIsSpeaking(false);
      synthesisRef.current = null;
    };

    synthesisRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [isMarathi]);

  // Stop speech function
  const stopSpeech = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      synthesisRef.current = null;
    }
  }, []);

  // Read search results aloud
  const readSearchResults = useCallback((results, query) => {
    if (!results || results.length === 0) {
      const noResultsText = isMarathi() 
        ? 'कोणतेही परिणाम सापडले नाहीत' 
        : 'No results found';
      speakText(noResultsText);
      return;
    }

    // Build the text to read
    let textToRead = '';
    
    if (isMarathi()) {
      textToRead = `तुम्हाला ${results.length} परिणाम सापडले. `;
      results.slice(0, 5).forEach((result, index) => {
        textToRead += `${index + 1}. ${result.title}. `;
        if (result.description) {
          textToRead += `${result.description}. `;
        }
      });
      if (results.length > 5) {
        textToRead += `आणि ${results.length - 5} अधिक परिणाम. `;
      }
      textToRead += 'परिणाम निवडण्यासाठी क्लिक करा.';
    } else {
      textToRead = `Found ${results.length} results. `;
      results.slice(0, 5).forEach((result, index) => {
        textToRead += `${index + 1}. ${result.title}. `;
        if (result.description) {
          textToRead += `${result.description}. `;
        }
      });
      if (results.length > 5) {
        textToRead += `and ${results.length - 5} more results. `;
      }
      textToRead += 'Click to select a result.';
    }

    speakText(textToRead);
  }, [isMarathi, speakText]);

  // Perform search (placeholder implementation)
  const performSearch = useCallback(async (query, fromVoice = false) => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 150));

    const needle = query.trim().toLowerCase();
    if (!needle) {
      setSearchResults([]);
      setIsLoading(false);
      return;
    }

    const qTokens = tokenize(needle);

    // Merge static pages with dynamic terms from registry
    const dynamicEntries = getAllSearchEntries().map(e => {
      const terms = isMarathi() ? e.terms.mr : e.terms.en;
      // Extract page title from URL for better display
      const urlParts = e.url.split('/').filter(Boolean);
      const pageId = urlParts[urlParts.length - 1] || 'home';
      const staticPage = PAGES.find(p => p.id === pageId || p.url === e.url);
      
      return {
        id: e.id || pageId,
        url: e.url,
        title: staticPage?.title || { marathi: '', english: '' },
        description: staticPage?.description || { marathi: '', english: '' },
        keywords: Array.isArray(terms) ? terms : [terms].filter(Boolean),
        fullText: Array.isArray(terms) ? terms.join(' ') : (terms || '')
      };
    });

    // Create a Set of URLs from dynamic entries to avoid duplicates
    const dynamicUrls = new Set(dynamicEntries.map(e => e.url));
    
    // Only include static pages that aren't already in dynamic entries
    const staticPages = PAGES
      .filter(p => !dynamicUrls.has(p.url))
      .map(p => ({ ...p, fullText: '' }));
    
    // Merge without duplicates (dynamic entries take priority as they have content)
    const catalog = [...dynamicEntries, ...staticPages];

    const scored = catalog.map(p => {
      const title = (isMarathi() ? p.title.marathi : p.title.english) || '';
      const desc = (isMarathi() ? p.description?.marathi : p.description?.english) || '';
      const kw = Array.isArray(p.keywords) ? p.keywords.join(' ') : (p.keywords || '');
      const fullText = p.fullText || '';
      const hayAll = `${title} ${desc} ${kw} ${fullText}`;
      const hay = normalize(hayAll);
      const titleNorm = normalize(title);
      const descNorm = normalize(desc);
      
      let score = 0;
      
      // Track if this result will have highlights (yellow marks)
      // Results with exact matches will be highlighted
      let hasHighlights = false;
      let hasExactMatch = false; // Track if there's at least one exact match
      
      // Special handling: Map "Disclaimer" and "Privacy Policy" searches to /privacy
      const isPrivacyPage = p.url === '/privacy';
      const normalizedQuery = needle.trim();
      if (isPrivacyPage && (
        normalizedQuery === 'disclaimer' || 
        normalizedQuery === 'privacy policy' ||
        normalizedQuery.includes('disclaimer') ||
        normalizedQuery.includes('privacy policy')
      )) {
        score += 30; // Very high score to ensure it appears first
        hasHighlights = true;
        hasExactMatch = true;
      }
      
      // Prioritize exact matches (which will be highlighted in yellow)
      // Exact match in title gets highest priority
      if (titleNorm.includes(needle)) {
        score += 20; // Very high score for exact title match
        hasHighlights = true;
        hasExactMatch = true;
      } else if (titleNorm === needle) {
        score += 25; // Perfect title match gets even higher
        hasHighlights = true;
        hasExactMatch = true;
      }
      
      // Exact match in description gets high priority
      if (descNorm.includes(needle)) {
        score += 15;
        hasHighlights = true;
        hasExactMatch = true;
      }
      
      // Exact full text match
      if (hay.includes(needle)) {
        score += 10;
        hasHighlights = true;
        hasExactMatch = true;
      }
      
      // Token-based scoring (exact token matches get highlighted)
      for (const qt of qTokens) {
        // Exact token match in title (will be highlighted)
        if (titleNorm.includes(qt)) {
          score += 8;
          hasHighlights = true;
          hasExactMatch = true;
          continue;
        }
        // Exact token match in description (will be highlighted)
        if (descNorm.includes(qt)) {
          score += 6;
          hasHighlights = true;
          hasExactMatch = true;
          continue;
        }
        // Exact token match in full text (will be highlighted in fullText)
        if (hay.includes(qt)) {
          score += 4;
          hasHighlights = true;
          hasExactMatch = true;
          continue;
        }
        // REMOVED FUZZY MATCHING - Only use exact matches
        // This prevents irrelevant results from appearing
      }
      
      return { p, score, title, desc, hasHighlights, hasExactMatch };
    })
    .filter(x => {
      // Only show results that have at least one exact match
      // AND have a minimum score of 4 (at least one token match in full text)
      return x.hasExactMatch && x.score >= 4;
    })
    .sort((a, b) => {
      // First sort by whether it has highlights (highlighted results first)
      if (a.hasHighlights !== b.hasHighlights) {
        // If a has highlights and b doesn't, a comes first (return negative)
        // If b has highlights and a doesn't, b comes first (return positive)
        return a.hasHighlights ? -1 : 1;
      }
      // Then sort by score (higher score first)
      return b.score - a.score;
    })
    .slice(0, 10);

    // Deduplicate final results by URL (extra safety)
    const seenUrls = new Set();
    const uniqueResults = scored
      .map(x => {
        // Generate fallback title from URL if title is empty
        let title = x.title;
        if (!title || title.trim() === '') {
          const url = x.p.url;
          // Check if it's an external URL
          if (url.startsWith('http://') || url.startsWith('https://')) {
            try {
              // Extract domain name from external URL
              const urlObj = new URL(url);
              title = urlObj.hostname.replace('www.', ''); // Remove www. prefix if present
            } catch (e) {
              // Fallback: extract domain manually if URL parsing fails
              const match = url.match(/https?:\/\/(?:www\.)?([^/]+)/);
              title = match ? match[1] : url;
            }
          } else {
            // Internal URL: use last path segment
            const urlParts = url.split('/').filter(Boolean);
            const lastPart = urlParts[urlParts.length - 1] || 'home';
            // Convert URL part to readable title (e.g., "list-of-services" -> "List of Services")
            title = lastPart
              .split('-')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
          }
        }
        
        // Generate fallback description if empty
        let description = x.desc;
        const isExternal = x.p.url.startsWith('http://') || x.p.url.startsWith('https://');
        
        // Special case: If searching for "RTS" and result is Right to Service page, show "RTS"
        const isRightToService = x.p.url === '/right-to-service';
        const isRTSQuery = needle === 'rts' || qTokens.some(token => token === 'rts');
        
        if (isExternal) {
          // For external links, always show "External Link"
          description = isMarathi() ? 'बाह्य लिंक' : 'External Link';
        } else if (isRightToService && isRTSQuery) {
          // For Right to Service page when searching for RTS, show "RTS"
          description = 'RTS';
        } else if (!description || description.trim() === '') {
          // For internal pages, use default description
          description = isMarathi() ? 'पृष्ठ' : 'Page';
        }
        
        return {
          id: x.p.id,
          title: title,
          description: description,
          url: x.p.url,
          type: 'page'
        };
      })
      .filter(result => {
        if (seenUrls.has(result.url)) {
          return false;
        }
        seenUrls.add(result.url);
        return true;
      });

    setSearchResults(uniqueResults);
    setIsLoading(false);

    // If search was triggered by voice, read results aloud
    if (fromVoice && uniqueResults.length > 0) {
      // Small delay to ensure UI is updated
      setTimeout(() => {
        readSearchResults(uniqueResults, query);
      }, 300);
    }
  }, [isMarathi, PAGES, normalize, tokenize, readSearchResults]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = isMarathi() ? 'mr-IN' : 'en-US';
      
      recognition.onstart = () => {
        setIsListening(true);
      };
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setSearchQuery(transcript);
        if (transcript.length > 2) {
          performSearch(transcript, true); // Pass true to indicate voice search
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
      
      recognition.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current = recognition;
      setSpeechSupported(true);
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      stopSpeech(); // Clean up speech on unmount
    };
  }, [isMarathi, performSearch, stopSpeech]);

  // Clean up speech on component unmount or search close
  useEffect(() => {
    return () => {
      stopSpeech();
    };
  }, [stopSpeech]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
        setSearchResults([]);
        if (isListening && recognitionRef.current) {
          recognitionRef.current.stop();
        }
        stopSpeech();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isListening, stopSpeech]);

  // Focus input when search opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.length > 2) {
      performSearch(query, false);
    } else {
      setSearchResults([]);
      stopSpeech(); // Stop speech if user is typing
    }
    setActiveIndex(-1);
  };

  // Handle search result click
  const handleResultClick = (result) => {
    // Read the selected result before navigating
    const resultText = isMarathi()
      ? `${result.title}. ${result.description || ''} निवडले.`
      : `${result.title}. ${result.description || ''} selected.`;
    
    speakText(resultText);
    
    // Wait a bit before navigating (optional - you can remove this if you want immediate navigation)
    setTimeout(() => {
      const isExternal = result.url.startsWith('http://') || result.url.startsWith('https://');
      
      if (isExternal) {
        showWarning(result.url);
      } else {
        window.location.href = result.url;
      }
      
      setIsOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      stopSpeech();
    }, 1000); // 1 second delay to allow speech to start
  };

  // Toggle search bar
  const toggleSearch = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      setActiveIndex(-1);
      if (isListening && recognitionRef.current) {
        recognitionRef.current.stop();
      }
      stopSpeech();
      return;
    }
    if (!searchResults || searchResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = prev + 1;
        return next >= searchResults.length ? 0 : next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? searchResults.length - 1 : next;
      });
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < searchResults.length) {
        handleResultClick(searchResults[activeIndex]);
      } else if (searchResults.length > 0) {
        handleResultClick(searchResults[0]);
      }
    }
  };

  // Toggle microphone
  const toggleMicrophone = () => {
    if (!speechSupported) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      stopSpeech(); // Stop any ongoing speech
    } else {
      stopSpeech(); // Stop any ongoing speech before starting new recognition
      recognitionRef.current.start();
    }
  };

  return (
    <div className="search-bar" ref={searchRef}>
      {/* Search Toggle Button */}
      <button
        className="search-toggle"
        onClick={toggleSearch}
        aria-label={t('search')}
        title={t('search')}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
      </button>

      {/* Search Input and Results */}
      {isOpen && (
        <div className="search-dropdown">
          <div className="search-input-container">
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder={t('search')}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyPress}
            />
            <div className="search-input-actions">
              {speechSupported && (
                <button
                  className={`mic-button ${isListening ? 'listening' : ''}`}
                  onClick={toggleMicrophone}
                  type="button"
                  aria-label={isListening ? 'Stop listening' : 'Start voice search'}
                  title={isListening ? 'Stop listening' : 'Start voice search'}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                    <line x1="12" y1="19" x2="12" y2="23"></line>
                    <line x1="8" y1="23" x2="16" y2="23"></line>
                  </svg>
                </button>
              )}
              
              {/* Add speaker button to stop/control speech */}
              {isSpeaking && (
                <button
                  className="speaker-button speaking"
                  onClick={stopSpeech}
                  type="button"
                  aria-label="Stop reading"
                  title="Stop reading results"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                    <line x1="23" y1="9" x2="17" y2="15"></line>
                    <line x1="17" y1="9" x2="23" y2="15"></line>
                  </svg>
                </button>
              )}

              {!speechSupported && (
                <span
                  className="voice-hint"
                  title="Voice search not supported in this browser"
                  aria-label="Voice search not supported"
                >
                  
                </span>
              )}
              {isLoading && (
                <div className="search-loading">
                  <div className="spinner"></div>
                </div>
              )}
            </div>
          </div>

          {/* Search Results */}
              {searchResults.length > 0 && (
            <div className="search-results">
                  {searchResults.map((result, idx) => (
                <div
                      key={result.id}
                      className={`search-result ${idx === activeIndex ? 'active' : ''}`}
                  onClick={() => handleResultClick(result)}
                >
                  <div className="result-title">{renderHighlighted(result.title, searchQuery)}</div>
                  {result.description && (
                    <div className="result-description">{renderHighlighted(result.description, searchQuery)}</div>
                  )}
                  {/* Hide type label - not needed as all results are pages */}
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {searchQuery.length > 2 && searchResults.length === 0 && !isLoading && (
            <div className="search-no-results">
              <div className="no-results-text">
                {t('comingSoon')} - {t('search')} {t('comingSoon')}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
